import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Shield,
  Lock,
  Mail,
  Eye,
  EyeOff,
  AlertCircle,
  QrCode,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Button } from '@components/common/Button';
import { Input } from '@components/forms/Input';
import { authenticateInspector, createOTPSession } from '@mocks/verification';

export const InspectorLogin: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verifyHashcode = searchParams.get('verify');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    setLoading(true);

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const inspector = authenticateInspector(email, password);

    if (!inspector) {
      toast.error('Invalid credentials or account suspended');
      setLoading(false);
      return;
    }

    // Generate OTP
    const otp = createOTPSession(inspector.id);
    
    toast.success(`Welcome, ${inspector.full_name}!`);
    toast.success(`Your OTP is: ${otp} (valid for 10 minutes)`, { duration: 8000 });

    // Store inspector info temporarily
    sessionStorage.setItem('pending_inspector', JSON.stringify(inspector));
    
    // Navigate to OTP verification
    if (verifyHashcode) {
      navigate(`/inspector/verify/${verifyHashcode}`);
    } else {
      navigate('/inspector/verify/pending');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-ncaa-primary to-blue-700 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8" />
            <h1 className="text-3xl font-bold">Inspector Portal</h1>
          </div>
          <p className="text-blue-100">Authorized NCAA License Verification System</p>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card>
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-ncaa-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-ncaa-primary" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Inspector Login</h2>
            <p className="text-gray-600">
              Enter your credentials to verify license details
            </p>
          </div>

          {verifyHashcode && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <QrCode className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900 mb-1">License Queued for Verification</p>
                  <p className="text-xs text-blue-700">
                    After login, you will be prompted to enter an OTP to view the full license details.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="inspector@ncaa.gov.ng"
                icon={<Mail className="h-4 w-4" />}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  icon={<Lock className="h-4 w-4" />}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              fullWidth
              loading={loading}
              icon={<Shield className="h-4 w-4" />}
            >
              Login as Inspector
            </Button>
          </form>

          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Demo Credentials</p>
                <p className="text-xs mb-2">For testing purposes, use:</p>
                <ul className="text-xs space-y-1">
                  <li>• Email: <code className="bg-yellow-100 px-1 rounded">inspector@ncaa.gov.ng</code></li>
                  <li>• Password: <code className="bg-yellow-100 px-1 rounded">inspector123</code></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Return to Home
            </button>
          </div>
        </Card>

        <div className="mt-6 text-center">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
            <Shield className="h-4 w-4" />
            <span>Secured by Nigerian Civil Aviation Authority (NCAA)</span>
          </div>
        </div>
      </div>
    </div>
  );
};
